// src/contexts/nobishiro-dashboard/TrizDashboard.jsx (エラー修正・リファクタリング完了版)
import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
// ▼▼▼ 修正点1: 不足していたアイコンをすべて追加します ▼▼▼
import {
    User, Brain, Users, Puzzle, SlidersHorizontal, Zap, BarChart3, Target,
    RefreshCcw, Lightbulb, BrainCircuit, Rocket, Wrench, CreditCard, ArrowRight,
    CalendarCheck, Menu, X, LogOut, Layers, Route, BookOpen
} from 'lucide-react';
import { SafeIcon } from './SVGErrorBoundary';
import DashboardErrorBoundary from './DashboardErrorBoundary';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import TabBar from '../../components/ui/TabBar';
import Loader from '../../components/ui/Loader';
import ErrorDisplay from '../../components/ui/ErrorDisplay';
import Modal from '../../components/ui/Modal';
import SummaryTab from '../../components/dashboard/tabs/SummaryTab';
import TeamTab from '../../components/dashboard/tabs/TeamTab';
import NeurodiversityProfile from '../../components/dashboard/NeurodiversityProfile';
import TRIZProblemAnalyzer from '../../pages/TRIZProblemAnalyzer';
import PlateauGrowthAnalysis from './PlateauGrowthAnalysis';
import { useUserProfile } from './useUserProfile';
import { useAuth } from '../../AuthContext';
import { getAllUsers } from '../../services/userService';
import { useSubscription } from '../../hooks/useSubscription';
import AchievementFeedTab from '../../components/dashboard/tabs/AchievementFeedTab';
import BetaBanner from '../../components/beta/BetaBanner';
import OnboardingFlow from '../../components/onboarding/OnboardingFlow';
import FeedbackWidget from '../../components/beta/FeedbackWidget';

const SummaryCard = ({ children, className = '' }) => (<div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}> {children} </div>);

const PlanInfoCard = () => {
    const { plan, isFree, loading } = useSubscription();

    if (loading) return <div className="text-xs text-gray-400">Loading...</div>;

    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <span className={`font-bold ${isFree ? 'text-gray-700' : 'text-indigo-600'}`}>
                    {plan === 'free' ? 'Free Plan' : plan === 'student' ? 'Student Plan' : plan === 'standard' ? 'Standard Plan' : 'Business Plan'}
                </span>
                {isFree && (
                    <Link to="/subscription" className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded font-bold hover:bg-indigo-200">
                        UP
                    </Link>
                )}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                <div className={`h-1.5 rounded-full ${isFree ? 'w-1/3 bg-gray-400' : 'w-full bg-indigo-500'}`}></div>
            </div>
            <p className="text-[10px] text-gray-500">
                {isFree ? 'Limited features' : 'All features unlocked'}
            </p>
        </div>
    );
};

const DashboardContent = () => {
    const { user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [allUserProfiles, setAllUserProfiles] = useState({});
    const [teamDataLoading, setTeamDataLoading] = useState(true);
    const [teamDataError, setTeamDataError] = useState(null);

    useEffect(() => {
        const fetchAllUsers = async () => {
            if (isAdmin) {
                try {
                    setTeamDataLoading(true);
                    const usersArray = await getAllUsers();
                    const profilesObject = usersArray.reduce((acc, user) => {
                        acc[user.id] = user;
                        return acc;
                    }, {});
                    setAllUserProfiles(profilesObject);
                    setTeamDataError(null);
                } catch (e) {
                    console.error("全ユーザーのプロファイル取得に失敗しました:", e);
                    setTeamDataError(e.message);
                } finally {
                    setTeamDataLoading(false);
                }
            } else {
                setTeamDataLoading(false);
            }
        };

        fetchAllUsers();
    }, [isAdmin]);

    const [targetUserId, setTargetUserId] = useState(user?.uid || null);
    const { profile, loading: profileLoading, error: profileError, updateProfile } = useUserProfile(targetUserId);
    const [activeTab, setActiveTab] = useState('summary');

    useEffect(() => { const params = new URLSearchParams(location.search); const tab = params.get('tab'); if (tab) { setActiveTab(tab) }; }, [location.search]);
    useEffect(() => { if (user && !targetUserId) { setTargetUserId(user.uid); } }, [user, targetUserId]);

    const mainTabs = [{ id: 'summary', label: 'サマリー', Icon: Brain }, { id: 'patterns', label: '成長パターン', Icon: RefreshCcw }, { id: 'neuro', label: '認知特性', Icon: SlidersHorizontal }, { id: 'team', label: 'チーム', Icon: Users, badge: 'NEW' }, { id: 'triz', label: 'TRIZ問題分析', Icon: Puzzle }, { id: 'achievements', label: '達成フィード', Icon: Zap, badge: 'NEW' },];

    const [onboardingDone, setOnboardingDone] = React.useState(null);

    React.useEffect(() => {
      if (!profile) return;
      if (profile.onboardingCompleted === false) {
        setOnboardingDone(false);
      } else {
        setOnboardingDone(true);
      }
    }, [profile]);

    const renderTabContent = () => {
        if (profileLoading || onboardingDone === null) return <Loader message="プロファイル読込中..." />;
        // オンボーディング未完了（新規ユーザー）
        if (onboardingDone === false) {
          return <OnboardingFlow onComplete={() => setOnboardingDone(true)} />;
        }
        if (profileError) return <ErrorDisplay title="プロファイルエラー" message={profileError.message} />
        if (!profile) return <ErrorDisplay message="プロファイルデータがありません。" />;

        if (activeTab === 'patterns') {
            if (profile.growthWallProfile) { return <PlateauGrowthAnalysis profile={profile} />; }
            else { return (<SummaryCard><div className="text-center p-6"><h2 className="text-xl font-bold text-gray-800">あなたの「成長の壁」を診断します</h2><p className="text-gray-600 mt-2 mb-6">現在の成長フェーズを特定し、乗り越えるためのヒントを得ましょう。</p><button onClick={() => navigate('/wall-diagnosis')} className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700">「成長の壁」診断を開始</button></div></SummaryCard>); }
        }

        switch (activeTab) {
            case 'summary': return <SummaryTab profile={profile} onTabChange={setActiveTab} updateProfile={updateProfile} />;
            case 'team': return <TeamTab currentUserProfile={profile} allUserProfiles={allUserProfiles} teamDataLoading={teamDataLoading} isAdmin={isAdmin} error={teamDataError} />;
            case 'neuro': return <SummaryCard><NeurodiversityProfile profileData={profile.neurodiversityProfile} /></SummaryCard>;
            case 'triz': return <SummaryCard><TRIZProblemAnalyzer /></SummaryCard>;
            case 'achievements': return <AchievementFeedTab />;
            default: return <SummaryTab profile={profile} onTabChange={setActiveTab} updateProfile={updateProfile} />;
        }
    };

    const { isFree } = useSubscription();
    const { signOut } = useAuth();

    return (
        <>
            <DashboardHeader targetUserId={targetUserId} setTargetUserId={setTargetUserId} isAdmin={isAdmin} allUserProfiles={allUserProfiles} signOut={signOut} />
            <main className="mt-4">
                {isFree && (
                    <div className="mb-6 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-4 sm:p-6 text-white shadow-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <Zap className="w-5 h-5 text-yellow-300" />
                                Standardプランで成長を加速させる
                            </h2>
                            <p className="text-indigo-100 text-sm mt-1">
                                AIによる詳細分析、無制限の診断、40の法則全閲覧など、全ての機能を開放しましょう。
                            </p>
                        </div>
                        <Link
                            to="/subscription?plan=standard"
                            className="bg-white text-indigo-600 px-5 py-2.5 rounded-lg font-bold text-sm shadow-md hover:bg-indigo-50 transition-colors whitespace-nowrap"
                        >
                            Standardにアップグレード
                        </Link>
                    </div>
                )}

                <TabBar tabs={mainTabs} activeTab={activeTab} setActiveTab={(tabId) => navigate(`/dashboard?tab=${tabId}`)} />
                <div className="mt-6">{renderTabContent()}</div>
            </main>
        </>
    )
}

const TrizDashboard = ({ children }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const location = useLocation();
    const { signOut } = useAuth();

    useEffect(() => { setIsSidebarOpen(false); }, [location]);

    const sidebarLinks = [
        { path: '/dashboard', label: 'ダッシュボード', icon: BarChart3 },
        { path: '/goal-setting', label: '目標設定', icon: Target },
        { path: '/personal-roadmap', label: 'パーソナルロードマップ', icon: Rocket, badge: 'STD' },
        { path: '/diagnosis', label: 'NOBISHIRO診断', icon: Puzzle },
        // ▼▼▼ 修正点2: パスに / を追加します ▼▼▼
        { path: '/wall-diagnosis', label: '成長の壁 診断', icon: Layers },
        { path: '/subscription', label: 'プラン管理', icon: CreditCard },
        { path: '/team', label: 'チーム管理', icon: Users, badge: 'BIZ' },
        { path: '/guide', label: '使い方ガイド', icon: BookOpen },
        { path: '/referral', label: '仲間を招待', icon: Users },
    ];

    return (
        <DashboardErrorBoundary>
            <div className="min-min-h-screen flex bg-gray-50">
                <aside className={`fixed lg:relative z-30 w-64 bg-white shadow-lg flex-shrink-0 flex flex-col h-full transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
                    <div className="p-4 border-b h-16 flex items-center justify-between">
                        <Link to="/dashboard" className="flex items-center gap-2"><div className="bg-blue-600 p-2 rounded-lg"><SafeIcon IconComponent={Brain} className="w-5 h-5 text-white" /></div><h1 className="text-xl font-bold text-blue-700">NOBISHIRO</h1></Link>
                        <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden p-2 text-gray-500 hover:text-gray-800"><X className="w-6 h-6" /></button>
                    </div>
                    <nav className="p-4 flex-grow">
                        <ul>{sidebarLinks.map(link => (
                            <li key={link.path}>
                                <Link to={link.path} className={`relative flex items-center gap-3 px-3 py-3 my-1 rounded-lg transition-colors text-sm font-medium ${location.pathname === link.path ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}>
                                    <SafeIcon IconComponent={link.icon} className="w-5 h-5" />
                                    <span>{link.label}</span>
                                    {link.badge && (
                                        <span className="ml-auto text-xs bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full font-semibold">
                                            {link.badge}
                                        </span>
                                    )}
                                </Link>
                            </li>
                        ))}</ul>
                    </nav>

                    {/* Subscription Info Card */}
                    <div className="p-4 border-t">
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Current Plan</p>
                            <PlanInfoCard />
                        </div>
                    </div>
                </aside>

                <div className="flex-1 flex flex-col w-full overflow-y-auto">
                    <header className="sticky top-0 bg-gray-50/80 backdrop-blur-sm z-10 lg:hidden p-2 border-b flex justify-between items-center">
                        <button onClick={() => setIsSidebarOpen(true)} className="p-2 text-gray-600 hover:text-gray-900"><Menu className="w-6 h-6" /></button>
                        <div className="pr-2"><button onClick={signOut} className="p-2 text-gray-600 hover:text-gray-900"><LogOut className="w-5 h-5" /></button></div>
                    </header>
                    <div className="flex-1">
                        <div className="w-full h-full p-4 sm:p-6 lg:p-8">
                            {children || <DashboardContent />}
                        </div>
                    </div>
                    <footer className="w-full max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 pt-6 border-t border-gray-200 text-center text-xs text-gray-500"><p>NOBISHIRO能力診断ダッシュボード © {new Date().getFullYear()}</p></footer>
                </div>
            </div>
        </DashboardErrorBoundary>
    );
};


export default TrizDashboard;